import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { LayoutDashboard, Home, Calendar, Mail, LogOut, Building2, UserCog } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function AdminLayout() {
  const { user, checking, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!checking && (!user || user === false || user.role !== "admin")) {
      navigate("/login", { state: { from: "/admin" } });
    }
  }, [user, checking, navigate]);

  if (checking || !user || user === false) {
    return <div className="text-center py-32 text-[hsl(100_3%_30%)]">Loading…</div>;
  }

  const links = [
    { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
    { to: "/admin/properties", label: "Properties", icon: Building2 },
    { to: "/admin/bookings", label: "Bookings", icon: Calendar },
    { to: "/admin/inquiries", label: "Inquiries", icon: Mail },
    { to: "/admin/profile", label: "Profile", icon: UserCog },
  ];

  return (
    <div className="mx-auto max-w-7xl px-6 md:px-10 py-8" data-testid="admin-layout">
      <div className="mb-8 flex items-end justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-[hsl(10_51%_55%)]">Admin</p>
          <h1 className="font-serif-display text-3xl sm:text-4xl mt-2">NewDayStays control room</h1>
        </div>
        <Button variant="outline" size="sm" onClick={() => logout().then(() => navigate("/"))} data-testid="admin-logout">
          <LogOut className="h-4 w-4 mr-2" /> Log out
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
        <aside className="lg:col-span-1">
          <nav className="space-y-1 border border-[hsl(165_37%_17%/0.1)] rounded-md bg-white p-2" data-testid="admin-sidebar">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                data-testid={`admin-nav-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm ${
                    isActive ? "bg-[hsl(165_37%_17%)] text-white" : "text-[hsl(90_4%_10%)] hover:bg-[hsl(42_22%_92%)]"
                  }`
                }
              >
                <l.icon className="h-4 w-4" /> {l.label}
              </NavLink>
            ))}
            <NavLink to="/" className="flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm text-[hsl(100_3%_30%)] hover:bg-[hsl(42_22%_92%)]">
              <Home className="h-4 w-4" /> Back to site
            </NavLink>
          </nav>
        </aside>
        <main className="lg:col-span-4">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
